class MegaMenu {
    constructor(selector = ".mega-nav") {
        this.megaNav = document.querySelector(selector);
        if (!this.megaNav) return;

        // All trigger buttons (top-level) using popover API
        this.triggers = this.megaNav.querySelectorAll("button[popovertarget]");
        if (this.triggers.length === 0) return;

        this.openPopover = null;
        this.openTrigger = null;
        this.hoverTimeouts = new Map();

        this.initializeBackdrop();
        this.attachTriggerEvents();
    }

    // Helper: Get transition settings from CSS custom properties
    getTransitionSettings(popover, type = "leave", isMenuSwitch = false) {
        const computedStyle = getComputedStyle(popover);
        let suffix;

        if (isMenuSwitch) {
            // Use dedicated switch variables when switching menus
            suffix = type === "enter" ? "switch-enter" : "switch-leave";
        } else {
            // Normal open/close - always use 'enter' duration for consistent timing
            suffix = "enter";
        }

        return {
            duration: computedStyle.getPropertyValue(`--mega-menu-popover-transition-duration-${suffix}`) || "425ms",
            easing:
                computedStyle.getPropertyValue(`--mega-menu-popover-transition-timing-${suffix}`) ||
                "cubic-bezier(.65,0,.35,1)",
            properties: computedStyle.transitionProperty,
        };
    }

    // Helper: Apply transition styles to popover
    applyTransitionStyles(popover, { duration, easing, properties }) {
        popover.style.transition = "";
        popover.style.transitionDuration = duration;
        popover.style.transitionTimingFunction = easing;
        popover.style.transitionProperty = properties;
    }

    // Helper: Clean up all inline transition styles
    cleanupTransitionStyles(popover) {
        popover.style.transition = "";
        popover.style.transitionProperty = "";
        popover.style.transitionDuration = "";
        popover.style.transitionTimingFunction = "";
        popover.style.maxHeight = "";
    }

    // Helper: Measure popover's natural height
    measureHeight(popover) {
        const currentMaxHeight = popover.style.maxHeight;
        popover.style.maxHeight = "none";
        const height = popover.scrollHeight;
        popover.style.maxHeight = currentMaxHeight;
        return height;
    }

    // Helper: Setup transition end listener
    setupTransitionEndListener(popover, callback) {
        const onEnd = (e) => {
            if (e.propertyName !== "max-height") return;
            popover.removeEventListener("transitionend", onEnd);
            callback();
        };
        popover.addEventListener("transitionend", onEnd);
        return onEnd;
    }

    initializeBackdrop() {
        this.globalBackdrop = document.getElementById("mega-menu-global-backdrop");
        if (!this.globalBackdrop) {
            this.globalBackdrop = document.createElement("div");
            this.globalBackdrop.id = "mega-menu-global-backdrop";
            this.globalBackdrop.className = "mega-menu-backdrop";
            document.body.appendChild(this.globalBackdrop);
        }
    }

    // Backdrop
    showBackdrop() {
        if (this.globalBackdrop && !this.globalBackdrop.classList.contains("active")) {
            this.globalBackdrop.classList.add("active");
            document.body.classList.add("mega-menu-open");
        }
    }
    hideBackdrop(force = false) {
        if (!this.globalBackdrop) return;
        if (force || !document.querySelector(".mega-menu-popover:popover-open")) {
            this.globalBackdrop.classList.remove("active");
            document.body.classList.remove("mega-menu-open");
        }
    }

    showPopover(trigger) {
        const popover = document.getElementById(trigger.getAttribute("popovertarget"));
        if (!popover) return;

        // If this popover is currently closing, cancel the close and reopen it
        if (popover.__closing) {
            this.cancelCloseAnimation(popover);
            // Reopen it (it's still :popover-open so we just need to animate back to full height)
            this.reopenClosingPopover(popover);
            return;
        }

        // Already open -> do nothing
        if (this.openPopover === popover) return;

        // Store reference to previous popover before closing it
        const previousPopover = this.openPopover;

        // Only cancel close animation if the NEW popover we want to open was stuck closing
        // (Don't interfere with the previous popover's normal close animation)
        if (popover.__closing && popover !== previousPopover) {
            this.cancelCloseAnimation(popover);
        }

        // Close previous (this will animate normally)
        if (previousPopover && previousPopover !== popover) {
            this.hidePopover(previousPopover, true); // keep backdrop

            // Wait for the close animation to complete before opening new popover
            const waitForClose = () => {
                if (previousPopover.__closing) {
                    // Still closing, check again in next frame
                    requestAnimationFrame(waitForClose);
                } else {
                    // Previous popover finished closing, now open the new one with switch timing
                    this.openNewPopover(popover, true); // isMenuSwitch = true
                }
            };
            requestAnimationFrame(waitForClose);
            return; // Exit early, openNewPopover will handle the rest
        }

        // No previous popover, open immediately
        this.openNewPopover(popover);
    }

    openNewPopover(popover, isMenuSwitch = false) {
        const transitionSettings = this.getTransitionSettings(popover, "enter", isMenuSwitch);

        // Prepare starting collapsed state for height animation
        popover.style.transition = "none";
        popover.style.maxHeight = "0px";
        popover.classList.add("animating-height");

        // The beforetoggle event will handle backdrop, active states, and tab initialization
        popover.showPopover();

        // Next frame: measure and animate
        requestAnimationFrame(() => {
            const targetHeight = this.measureHeight(popover);
            popover.style.setProperty("--mega-menu-target-height", targetHeight + "px");

            // Reset back to 0 then animate
            popover.style.maxHeight = "0px";
            // Force reflow
            // eslint-disable-next-line no-unused-expressions
            popover.offsetHeight;

            this.applyTransitionStyles(popover, transitionSettings);
            // Start animation to target height
            popover.style.maxHeight = targetHeight + "px";

            this.setupTransitionEndListener(popover, () => {
                // Allow natural growth after animation completes
                popover.style.maxHeight = "none";
                popover.classList.remove("animating-height");
                this.cleanupTransitionStyles(popover);
            });
        });
    }

    reopenClosingPopover(popover) {
        const transitionSettings = this.getTransitionSettings(popover, "enter");

        // The popover is still open but partially collapsed, animate back to full height
        popover.classList.add("animating-height");

        const targetHeight = this.measureHeight(popover);
        this.applyTransitionStyles(popover, transitionSettings);

        // Animate to target height
        requestAnimationFrame(() => {
            popover.style.maxHeight = targetHeight + "px";

            this.setupTransitionEndListener(popover, () => {
                // Allow natural growth after animation completes
                popover.style.maxHeight = "none";
                popover.classList.remove("animating-height");
                this.cleanupTransitionStyles(popover);
            });
        });
    }

    cancelCloseAnimation(popover) {
        if (popover.__closeListener) {
            popover.removeEventListener("transitionend", popover.__closeListener);
            popover.__closeListener = null;
        }
        popover.__closing = false;
        popover.classList.remove("animating-height");
        this.cleanupTransitionStyles(popover);
    }

    hidePopover(popover, keepBackdrop = false) {
        if (!popover) return;

        // If already closing, don't interfere
        if (popover.__closing) return;

        // Always remove active state immediately when hiding a popover
        if (this.openPopover === popover && this.openTrigger) {
            this.openTrigger.parentElement.classList.remove("is-active");
        }

        if (popover.matches(":popover-open")) {
            // Determine if this is a menu switch (keepBackdrop = true means we're switching)
            const isMenuSwitch = keepBackdrop;
            this.animateClose(popover, keepBackdrop, isMenuSwitch);
        } else {
            this.immediateCleanup(popover, keepBackdrop);
        }
    }

    animateClose(popover, keepBackdrop, isMenuSwitch = false) {
        popover.__closing = true;
        popover.classList.add("animating-height");

        const transitionSettings = this.getTransitionSettings(popover, "leave", isMenuSwitch);
        const currentHeight = popover.scrollHeight;

        // Set explicit height first (no transition yet)
        popover.style.transition = "none";
        popover.style.maxHeight = currentHeight + "px";

        // Force reflow to register the explicit height
        // eslint-disable-next-line no-unused-expressions
        popover.offsetHeight;

        // Re-enable transitions and animate to 0 in next frame
        requestAnimationFrame(() => {
            this.applyTransitionStyles(popover, transitionSettings);
            popover.style.maxHeight = "0px";
        });

        const onEnd = this.setupTransitionEndListener(popover, () => {
            // Hide popover after height animation completes (only if still open)
            if (popover.matches(":popover-open")) {
                popover.hidePopover();
            }
            this.finalizeClose(popover, keepBackdrop);
        });

        popover.__closeListener = onEnd;
    }

    immediateCleanup(popover, keepBackdrop) {
        if (popover.matches(":popover-open")) popover.hidePopover();
        this.finalizeClose(popover, keepBackdrop);
    }

    finalizeClose(popover, keepBackdrop) {
        // Only hide backdrop if not keeping it (not switching menus)
        if (!keepBackdrop) this.hideBackdrop();

        // Only clear openPopover reference if this was the currently open one
        if (this.openPopover === popover) {
            this.openPopover = null;
            this.openTrigger = null;
        }

        popover.__closing = false;
        popover.__closeListener = null;
        popover.classList.remove("animating-height");
        this.cleanupTransitionStyles(popover);
    }

    attachTriggerEvents() {
        // Handle only top-level menu items (direct children of the main menu)
        this.megaNav.querySelectorAll(".menu > li").forEach((li) => {
            const menuItem = li.querySelector("button, a");
            if (!menuItem) return;

            // Add hover states for top-level menu items only
            li.addEventListener("mouseenter", () => {
                li.classList.add("is-active");
            });

            // Unified method to handle leaving menu item
            const handleLeave = (isImmediate = false) => {
                const popover = menuItem.getAttribute("popovertarget")
                    ? document.getElementById(menuItem.getAttribute("popovertarget"))
                    : null;

                if (!isImmediate && popover && popover.matches(":popover-open")) {
                    // Delay for keyboard navigation into popover
                    setTimeout(() => {
                        if (!popover.contains(document.activeElement) && document.activeElement !== menuItem) {
                            li.classList.remove("is-active");
                        }
                    }, 0);
                } else {
                    // Immediate removal for mouse or no popover
                    li.classList.remove("is-active");
                }
            };

            // Mouse events
            li.addEventListener("mouseleave", () => handleLeave(true));

            // Keyboard events
            menuItem.addEventListener("focus", () => {
                li.classList.add("is-active");
            });

            menuItem.addEventListener("blur", () => handleLeave(false));
        });

        this.triggers.forEach((trigger) => {
            const li = trigger.parentElement; // hover bridge lives here
            const popover = document.getElementById(trigger.getAttribute("popovertarget"));
            if (!popover) return;

            // Hover enter
            li.addEventListener("mouseenter", () => {
                clearTimeout(li.hoverTimeout);
                li.hoverTimeout = setTimeout(() => this.showPopover(trigger), 50);
            });

            // Hover leave (li)
            li.addEventListener("mouseleave", () => {
                clearTimeout(li.hoverTimeout);

                // Remove active state immediately on mouse leave
                li.classList.remove("is-active");

                li.hoverTimeout = setTimeout(() => {
                    // Only close if pointer not over trigger, popover, or another trigger's li
                    if (!li.matches(":hover") && !popover.matches(":hover")) {
                        this.hidePopover(popover);
                    }
                }, 200);
            });

            // Popover hover management
            popover.addEventListener("mouseenter", () => {
                clearTimeout(li.hoverTimeout);
            });

            // Unified popover leave handling
            const handlePopoverLeave = () => {
                clearTimeout(li.hoverTimeout);

                li.hoverTimeout = setTimeout(() => {
                    const activeElement = document.activeElement;
                    const isInPopover = popover.contains(activeElement);
                    const isOnTrigger = activeElement === trigger || li.matches(":hover");

                    // Remove active state and close popover if completely left
                    if (!isInPopover && !isOnTrigger) {
                        li.classList.remove("is-active");
                        this.hidePopover(popover);
                    }
                }, 200);
            };

            popover.addEventListener("mouseleave", handlePopoverLeave);
            popover.addEventListener("focusout", handlePopoverLeave);

            // Click -> navigate (still acts as link substitute)
            trigger.addEventListener("click", (e) => {
                if (trigger.dataset.href) {
                    e.preventDefault();
                    if (trigger.dataset.target && trigger.dataset.target !== "_self") {
                        window.open(trigger.dataset.href, trigger.dataset.target);
                    } else {
                        window.location.href = trigger.dataset.href;
                    }
                }
            });

            // Handle Enter key to navigate instead of opening popover
            trigger.addEventListener("keydown", (e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();

                    // Navigate using the same logic as click handler
                    if (trigger.dataset.href) {
                        if (trigger.dataset.target && trigger.dataset.target !== "_self") {
                            window.open(trigger.dataset.href, trigger.dataset.target);
                        } else {
                            window.location.href = trigger.dataset.href;
                        }
                    }
                }
            });

            // Listen for native popover events to handle backdrop and active states
            popover.addEventListener("beforetoggle", (e) => {
                if (e.newState === "open") {
                    // Popover is opening - handle backdrop and active state
                    this.openPopover = popover;
                    this.openTrigger = trigger;
                    trigger.parentElement.classList.add("is-active");
                    this.showBackdrop();
                    this.initializeTabs(popover);
                } else if (e.newState === "closed") {
                    // Popover is closing - handle cleanup
                    if (this.openPopover === popover) {
                        this.openPopover = null;
                        this.openTrigger = null;
                    }
                    trigger.parentElement.classList.remove("is-active");

                    // Use setTimeout to check backdrop after other popovers may have opened
                    setTimeout(() => {
                        this.hideBackdrop();
                    }, 0);
                }
            });
        });

        // Cleanup on unload
        window.addEventListener("beforeunload", () => {
            this.hoverTimeouts?.forEach((t) => clearTimeout(t));
        });
    }

    initializeTabs(popover) {
        // If already initialized mark to avoid duplicate listeners
        if (!popover || popover.__tabsInitialized) return;
        const groups = popover.querySelectorAll("el-tab-group");
        groups.forEach((tabs) => {
            const buttons = tabs.querySelectorAll("el-tab-list button");
            const contents = tabs.querySelectorAll("el-tab-panels .tab-content");
            if (!buttons.length) return;

            const setActive = (i) => {
                tabs.setActiveTab(i);
                buttons.forEach((b, bi) => b.classList.toggle("active", bi === i));
                contents.forEach((c, ci) => c.classList.toggle("active", ci === i));
            };

            buttons.forEach((btn, i) => {
                btn.addEventListener("mouseover", () => {
                    clearTimeout(btn.hoverTimeout);
                    btn.hoverTimeout = setTimeout(() => setActive(i), 50);
                });
                btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    setActive(i);
                    if (btn.dataset.href) {
                        if (btn.dataset.target && btn.dataset.target !== "_self") {
                            window.open(btn.dataset.href, btn.dataset.target);
                        } else {
                            window.location.href = btn.dataset.href;
                        }
                    }
                });
            });
        });
        popover.__tabsInitialized = true;
    }
}

// Boot
document.addEventListener("DOMContentLoaded", () => new MegaMenu());
